/**
 * The strip under Then → Now → Goal.
 *
 * Its axis is progress toward the target, not time — the concept places the
 * intermediate scans at 8 %, 17 % and 29 % of the way, which are body-fat
 * positions rather than dates. A time axis would put the dots somewhere else
 * entirely and quietly contradict the "NOW" marker beside it.
 *
 * The travelled part is solid sage; what remains is dashed amber, the same
 * rule the goal column follows — nothing unmeasured is ever drawn solid.
 */
export const ProgressTimeline: React.FC<{
  /** 0…1 of the way from start to target. */
  fraction: number;
  /** Each earlier scan's own fraction, start excluded. */
  scanFractions?: number[];
  weeks?: number;
  targetBodyFat?: number;
}> = ({ fraction, scanFractions = [], weeks, targetBodyFat }) => {
  const pct = Math.max(0, Math.min(1, fraction)) * 100;

  return (
    <div
      data-testid="progress-timeline"
      className="relative mx-2 mt-4 h-[30px]"
      role="img"
      aria-label={`${Math.round(pct)} % of the way to the target${
        weeks !== undefined ? `, ${weeks} weeks in` : ''
      }`}
    >
      <div className="absolute inset-x-0 top-2 h-[3px] rounded-sm bg-divider" />
      <div
        className="absolute left-0 top-2 h-[3px] rounded-sm bg-primary"
        style={{ width: `${pct}%` }}
      />
      <div
        className="absolute right-0 top-2 h-[3px] rounded-sm"
        style={{
          left: `${pct}%`,
          background:
            'repeating-linear-gradient(90deg, hsl(var(--projected-line)) 0 6px, transparent 6px 11px)',
        }}
      />

      <div className="absolute left-0 top-[5px] h-[9px] w-[9px] rounded-full bg-foreground" />
      {scanFractions.map((f, i) => (
        <div
          key={i}
          className="absolute top-1.5 h-[7px] w-[7px] rounded-full bg-lean-pale"
          style={{ left: `${Math.max(0, Math.min(1, f)) * 100}%` }}
        />
      ))}
      <div
        className="absolute top-px h-[17px] w-[17px] -ml-2 rounded-full border-[3px] border-primary bg-card"
        style={{ left: `${pct}%` }}
      />
      <div className="absolute right-0 top-[5px] h-[9px] w-[9px] rounded-full border-[1.5px] border-dashed border-[hsl(var(--projected))] bg-card" />

      <div className="absolute left-0 top-[22px] font-mono text-[8.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
        Start
      </div>
      <div
        className="absolute top-[22px] -translate-x-1/2 whitespace-nowrap font-mono text-[8.5px] font-medium uppercase tracking-[0.08em] text-primary"
        style={{ left: `${pct}%` }}
      >
        Now{weeks !== undefined ? ` · ${weeks} weeks` : ''}
      </div>
      <div className="absolute right-0 top-[22px] whitespace-nowrap font-mono text-[8.5px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--projected))]">
        Goal{targetBodyFat !== undefined ? ` · ${targetBodyFat} %` : ''} · no date forecast
      </div>
    </div>
  );
};
